/** Minimum "Every N minutes" interval for scheduled jobs. */
export const MIN_MINUTELY_REPEAT_INTERVAL = 5;

/** Task picklist-style repeat frequency (maps to scheduler.repeat_type via API). */
export const REPEAT_FREQUENCY_OPTIONS = [
  { value: 'once', label: 'Does not repeat' },
  { value: 'minutely', label: 'Minutes' },
  { value: 'hourly', label: 'Hours' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

export function repeatIntervalUnitLabel(frequency) {
  switch (frequency) {
    case 'minutely':
      return 'minute(s)';
    case 'hourly':
      return 'hour(s)';
    case 'daily':
      return 'day(s)';
    case 'weekly':
      return 'week(s)';
    case 'monthly':
      return 'month(s)';
    case 'yearly':
      return 'year(s)';
    default:
      return '';
  }
}

export function validateRepeatInterval(frequency, intervalRaw) {
  const n = parseInt(intervalRaw, 10);
  if (frequency === 'minutely') {
    if (!Number.isFinite(n) || n < MIN_MINUTELY_REPEAT_INTERVAL) {
      return {
        error: `Repeat interval must be at least ${MIN_MINUTELY_REPEAT_INTERVAL} minutes.`,
      };
    }
  } else if (!Number.isFinite(n) || n < 1) {
    return { error: 'Repeat interval must be at least 1.' };
  }
  if (frequency === 'minutely' && n > 10080) {
    return { error: 'Minutes interval cannot exceed 10080 (7 days).' };
  }
  if (frequency === 'hourly' && n > 8760) {
    return { error: 'Hours interval cannot exceed 8760 (1 year).' };
  }
  return { interval: n };
}

/** Same keys as task repeat_on_day multipicklist (semicolon-separated in EditLayout). */
export const REPEAT_WEEKDAY_OPTIONS = [
  { key: 'sun', label: 'Sunday' },
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
];

export function mapRepeatTypeToFrequency(repeatType) {
  const t = repeatType != null ? String(repeatType).toLowerCase().trim() : 'none';
  if (t === 'none' || t === '') return 'once';
  return t;
}

/** Parse DB repeat_day_of_week → selected day keys for UI */
export function repeatDayOfWeekToKeys(repeatDayOfWeek) {
  if (repeatDayOfWeek == null || repeatDayOfWeek === '') return [];
  let nums = [];
  if (typeof repeatDayOfWeek === 'number') {
    nums = [repeatDayOfWeek];
  } else if (typeof repeatDayOfWeek === 'string') {
    try {
      const parsed = JSON.parse(repeatDayOfWeek);
      nums = Array.isArray(parsed) ? parsed : [parseInt(repeatDayOfWeek, 10)];
    } catch (_) {
      const n = parseInt(repeatDayOfWeek, 10);
      nums = Number.isNaN(n) ? [] : [n];
    }
  }
  const numToKey = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };
  return nums
    .filter((n) => n >= 0 && n <= 6)
    .map((n) => numToKey[n])
    .filter(Boolean);
}

export function keysToRepeatOnDay(keys) {
  return (keys || []).filter(Boolean).join(';');
}

/** Merge task-style repeat_at (HH:mm) into datetime-local value */
export function mergeRepeatAtIntoDatetimeLocal(datetimeLocal, repeatAt) {
  if (!datetimeLocal || !repeatAt || String(repeatAt).trim() === '') return datetimeLocal;
  const [datePart] = String(datetimeLocal).split('T');
  const time = String(repeatAt).trim().slice(0, 5);
  return `${datePart}T${time}`;
}

/** ISO / DB timestamp → value for datetime-local input (browser local wall time). */
export function isoToDatetimeLocal(iso) {
  if (iso == null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** start_date column or legacy next_run when column missing */
export function resolveStartDateFromRow(row) {
  if (!row) return '';
  if (row.start_date) return isoToDatetimeLocal(row.start_date);
  if (row.next_run) return isoToDatetimeLocal(row.next_run);
  return '';
}

/** end_date column or legacy config.endDate */
export function resolveEndDateFromRow(row) {
  if (!row) return '';
  if (row.end_date) return isoToDatetimeLocal(row.end_date);
  try {
    let c = row.config;
    if (typeof c === 'string') c = JSON.parse(c);
    if (c?.endDate) return isoToDatetimeLocal(c.endDate);
    if (c?.end_date) return isoToDatetimeLocal(c.end_date);
  } catch (_) {
    /* ignore */
  }
  return '';
}

export function extractRepeatAtFromIso(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Build API body repeat fields (task field names + scheduler columns).
 */
export function buildRepeatPayload(form) {
  const frequency = form.repeat_frequency || 'once';
  if (frequency !== 'once') {
    const { interval, error } = validateRepeatInterval(frequency, form.repeat_interval);
    if (error) return { error };
    const payload = {
      repeat_frequency: frequency,
      repeat_type: frequency,
      repeat_interval: interval,
    };
    if (frequency === 'weekly') {
      const keys = form.repeat_on_day_keys || [];
      if (keys.length === 0) {
        return { error: 'Select at least one day for weekly repeat.' };
      }
      payload.repeat_on_day = keysToRepeatOnDay(keys);
    }
    if (
      frequency !== 'minutely' &&
      frequency !== 'hourly' &&
      form.repeat_at
    ) {
      payload.repeat_at = form.repeat_at;
    }
    return { payload };
  }
  return {
    payload: {
      repeat_frequency: 'once',
      repeat_type: 'none',
      repeat_interval: 1,
    },
  };
}
